import React, { useEffect, useState } from "react";
import { API_BASE_URL } from "../../../../environment";
import { getLifecycleTypeName } from "../../../../utils/lifecycleUtils";

interface UserStats {
  user_id: number;
  user_name: string;
  role: string;
  companies: number;
  contacts: number;
  leads: number;
  pipelines: number;
  deals: number;
}

interface TeamStats {
  team_id: number;
  team_name: string;
  manager_name: string;
  team_members_count: number;
  companies: number;
  contacts: number;
  leads: number;
  pipelines: number;
  deals: number;
}

interface StageStats {
  user_id?: number;
  team_id?: number;
  user_name?: string;
  team_name?: string;
  role?: string;
  manager_name?: string;
  team_members_count?: number;
  [key: string]: any;
  stages: Record<string, number>;
}

interface DetailedStats {
  related_entity: string;
  lifecycle_type: string;
  user_stats?: StageStats[];
  team_stats?: StageStats[];
}

interface StatsTableProps {
  tenantId: string;
  isSalesUserTeamManager?: boolean;
}

type ViewMode = "sales-user" | "team";

const isUserStats = (item: UserStats | TeamStats): item is UserStats => {
  return "user_id" in item;
};

const isTeamStats = (item: UserStats | TeamStats): item is TeamStats => {
  return "team_id" in item;
};

const StatsTable: React.FC<StatsTableProps> = ({
  tenantId,
  isSalesUserTeamManager = false,
}) => {
  const [userStats, setUserStats] = useState<UserStats[]>([]);
  const [teamStats, setTeamStats] = useState<TeamStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailedStats, setDetailedStats] = useState<DetailedStats | null>(
    null
  );
  const [detailedLoading, setDetailedLoading] = useState(false);
  const [expandedEntity, setExpandedEntity] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(
    isSalesUserTeamManager ? "team" : "sales-user"
  );
  const [cachedDetailedStats, setCachedDetailedStats] = useState<
    Record<string, DetailedStats>
  >({});

  const fetchUserStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const authHeader = localStorage.getItem("authCred") || "";
      const response = await fetch(
        `${API_BASE_URL}/dashboard/complete-stats/?tenant_id=${tenantId}&identity=client_admin`,
        {
          headers: {
            Authorization: `Basic ${authHeader}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const salesUsers = (data.user_stats || []).filter(
        (user: UserStats) =>
          user.role.toLowerCase() !== "super admin" &&
          user.role.toLowerCase() !== "client admin"
      );
      setUserStats(salesUsers);

      setTimeout(() => preloadDetailedStats(), 100);
    } catch (err) {
      console.error("Error fetching user stats:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch user statistics"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const authHeader = localStorage.getItem("authCred") || "";
      const response = await fetch(
        `${API_BASE_URL}/dashboard/complete-stats/?tenant_id=${tenantId}&identity=client_admin&scope=team`,
        {
          headers: {
            Authorization: `Basic ${authHeader}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setTeamStats(data.team_stats || []);

      setTimeout(() => preloadDetailedStats(), 100);
    } catch (err) {
      console.error("Error fetching team stats:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch team statistics"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tenantId) {
      if (viewMode === "sales-user") {
        fetchUserStats();
      } else {
        fetchTeamStats();
      }
    }
  }, [tenantId, viewMode]);

  const entityTypes = [
    {
      key: "companies",
      label: "Companies",
      expandable: false,
      apiKey: "companies",
    },
    {
      key: "contacts",
      label: "Contacts",
      expandable: false,
      apiKey: "contacts",
    },
    { key: "leads", label: getLifecycleTypeName("LEAD"), expandable: true, apiKey: "lead" },
    {
      key: "pipelines",
      label: getLifecycleTypeName("PIPELINE"),
      expandable: true,
      apiKey: "pipeline",
    },
    { key: "deals", label: getLifecycleTypeName("DEAL"), expandable: true, apiKey: "deal" },
  ];

  const preloadDetailedStats = async () => {
    const expandableEntities = entityTypes.filter(
      (entity) => entity.expandable
    );
    const authHeader = localStorage.getItem("authCred") || "";
    const scopeParam = viewMode === "team" ? "&scope=team" : "";

    const cacheKey = `${viewMode}-${tenantId}`;

    const promises = expandableEntities.map(async (entity) => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/dashboard/complete-stats/?tenant_id=${tenantId}&identity=client_admin&related_entity=${entity.apiKey}${scopeParam}`,
          {
            headers: {
              Authorization: `Basic ${authHeader}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          return { entityKey: entity.key, data };
        }
        return null;
      } catch (err) {
        console.warn(`Failed to preload stats for ${entity.key}:`, err);
        return null;
      }
    });

    const results = await Promise.allSettled(promises);
    const newCache: Record<string, DetailedStats> = {};

    results.forEach((result, index) => {
      if (result.status === "fulfilled" && result.value) {
        const { entityKey, data } = result.value;
        newCache[entityKey] = data;
      }
    });

    setCachedDetailedStats(newCache);
  };

  const fetchDetailedStats = async (entityType: string) => {
    try {
      setDetailedLoading(true);
      setError(null);

      const authHeader = localStorage.getItem("authCred") || "";
      const scopeParam = viewMode === "team" ? "&scope=team" : "";
      const response = await fetch(
        `${API_BASE_URL}/dashboard/complete-stats/?tenant_id=${tenantId}&identity=client_admin&related_entity=${entityType}${scopeParam}`,
        {
          headers: {
            Authorization: `Basic ${authHeader}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setDetailedStats(data);
      const entity = entityTypes.find((e) => e.apiKey === entityType);
      setExpandedEntity(entity ? entity.key : entityType);
    } catch (err) {
      console.error("Error fetching detailed stats:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to fetch detailed statistics"
      );
    } finally {
      setDetailedLoading(false);
    }
  };

  const handleEntityClick = (entityType: string) => {
    if (expandedEntity === entityType) {
      setExpandedEntity(null);
      setDetailedStats(null);
    } else {
      if (cachedDetailedStats[entityType]) {
        setDetailedStats(cachedDetailedStats[entityType]);
        setExpandedEntity(entityType);
      } else {
        const entity = entityTypes.find((e) => e.key === entityType);
        if (entity && entity.apiKey) {
          fetchDetailedStats(entity.apiKey);
        }
      }
    }
  };

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    setExpandedEntity(null);
    setDetailedStats(null);
    setCachedDetailedStats({});
  };

  const currentStats = viewMode === "sales-user" ? userStats : teamStats;
  const isTeamView = viewMode === "team";

  return (
    <div className="col-md-12 d-flex">
      <div className="card flex-fill">
        <style>
          {`
            .cursor-pointer {
              cursor: pointer;
            }
            .hover-highlight:hover {
              background-color: rgba(0, 123, 255, 0.1) !important;
            }
          `}
        </style>
        <div className="card-header">
          <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <h4 className="mb-0 d-flex align-items-center" style={{ fontSize: "1.25rem" }}>
              <span className="db-chart-icon">
                <i className="ti ti-chart-infographic" />
              </span>
              Statistics
            </h4>
            {!isSalesUserTeamManager && (
              <div className="d-flex align-items-center">
                <label
                  className="me-2"
                  style={{ fontSize: "0.9rem", fontWeight: 500 }}
                >
                  View:
                </label>
                <select
                  className="form-select form-select-sm"
                  style={{ width: "20%", minWidth: "120px" }}
                  value={viewMode}
                  onChange={(e) =>
                    handleViewModeChange(e.target.value as ViewMode)
                  }
                >
                  <option value="sales-user">Sales User</option>
                  <option value="team">Team</option>
                </select>
              </div>
            )}
          </div>
        </div>
        {loading ? (
          <div className="card-body">
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-2">
                <small className="text-muted">Loading table data...</small>
              </div>
            </div>
          </div>
        ) : error ? (
          <div className="card-body">
            <div className="alert alert-danger" role="alert">
              <strong>Error:</strong> {error}
            </div>
          </div>
        ) : currentStats.length === 0 ? (
          <div className="card-body">
            <div
              className="table-responsive custom-table"
              style={{ overflowY: "auto" }}
            >
              <table className="table dataTable" id="user-stats-table">
                <thead className="thead-light">
                  <tr>
                    <th
                      className="text-start"
                      style={{ width: "20%", borderRight: "2px solid #dee2e6" }}
                    ></th>
                    <th className="text-center">
                      {isTeamView ? "Team" : "User"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td
                      colSpan={2}
                      className="text-center"
                      style={{ padding: "60px 20px" }}
                    >
                      <div className="text-center">
                        <i
                          className="ti ti-database-off text-muted mb-3"
                          style={{ fontSize: "3rem" }}
                        ></i>
                        <h5 className="text-muted">No Data Available</h5>
                        <p className="text-muted">
                          No {isTeamView ? "team" : "user"} statistics available
                        </p>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="card-body">
            <div
              className="table-responsive custom-table"
              style={{ overflowY: "auto" }}
            >
              <table className="table dataTable" id="user-stats-table">
                <thead className="thead-light">
                  <tr>
                    <th
                      className="text-start"
                      style={{ width: "20%", borderRight: "2px solid #dee2e6" }}
                    ></th>
                    {currentStats.map((item) => (
                      <th
                        key={
                          isTeamView
                            ? (item as TeamStats).team_id
                            : (item as UserStats).user_id
                        }
                        className="text-center"
                        style={{ minWidth: "150px" }}
                      >
                        <div>
                          <div className="fw-bold">
                            {isTeamView
                              ? (item as TeamStats).team_name
                              : (item as UserStats).user_name}
                          </div>
                          <small className="text-muted">
                            {isTeamView
                              ? `${(item as TeamStats).manager_name}`
                              : (item as UserStats).role}
                          </small>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {entityTypes.map((entity) => (
                    <React.Fragment key={entity.key}>
                      <tr
                        className={
                          entity.expandable
                            ? "cursor-pointer hover-highlight"
                            : ""
                        }
                        onClick={
                          entity.expandable
                            ? () => handleEntityClick(entity.key)
                            : undefined
                        }
                      >
                        <td
                          className="fw-bold text-start align-middle"
                          style={{
                            width: "20%",
                            borderRight: "2px solid #dee2e6",
                            fontSize: "1rem",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-start">
                            {entity.label}
                            {entity.expandable && (
                              <i
                                className={`ti ti-chevron-${expandedEntity === entity.key ? "up" : "down"
                                  } ms-2`}
                                style={{ fontSize: "0.8rem" }}
                              />
                            )}
                          </div>
                        </td>
                        {currentStats.map((item) => (
                          <td
                            key={`${isTeamView
                              ? (item as TeamStats).team_id
                              : (item as UserStats).user_id
                              }-${entity.key}`}
                            className="text-center align-middle"
                          >
                            <span>
                              {item[
                                entity.key as keyof (UserStats | TeamStats)
                              ] || 0}
                            </span>
                          </td>
                        ))}
                      </tr>

                      {expandedEntity === entity.key && (
                        <>
                          {detailedLoading ? (
                            <tr>
                              <td
                                colSpan={currentStats.length + 1}
                                className="text-center p-3"
                              >
                                <div className="d-flex justify-content-center align-items-center">
                                  <div
                                    className="spinner-border spinner-border-sm text-primary me-2"
                                    role="status"
                                  >
                                    <span className="visually-hidden">
                                      Loading...
                                    </span>
                                  </div>
                                  <span>Loading detailed statistics...</span>
                                </div>
                              </td>
                            </tr>
                          ) : detailedStats &&
                            detailedStats.related_entity === entity.apiKey ? (
                            <>
                              {(() => {
                                const statsArray = isTeamView
                                  ? detailedStats.team_stats
                                  : detailedStats.user_stats;
                                return (
                                  statsArray &&
                                  statsArray.length > 0 &&
                                  Object.keys(statsArray[0]?.stages || {}).map(
                                    (stage) => (
                                      <tr key={stage} className="table-light">
                                        <td
                                          className="fw-bold text-start align-middle"
                                          style={{
                                            width: "20%",
                                            borderRight: "2px solid #dee2e6",
                                            paddingLeft: "40px",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          <span style={{ fontSize: "0.95rem" }}>
                                            {stage}
                                          </span>
                                        </td>
                                        {currentStats.map((item) => {
                                          const detailedItem = statsArray.find(
                                            (u) =>
                                              isTeamView
                                                ? u.team_id ===
                                                (item as TeamStats).team_id
                                                : u.user_id ===
                                                (item as UserStats).user_id
                                          );
                                          return (
                                            <td
                                              key={`${isTeamView
                                                ? (item as TeamStats).team_id
                                                : (item as UserStats).user_id
                                                }-${stage}`}
                                              className="text-center align-middle"
                                            >
                                              <span>
                                                {detailedItem?.stages[stage] ||
                                                  0}
                                              </span>
                                            </td>
                                          );
                                        })}
                                      </tr>
                                    )
                                  )
                                );
                              })()}
                              {(() => {
                                const statsArray = isTeamView
                                  ? detailedStats.team_stats
                                  : detailedStats.user_stats;
                                return (
                                  (!statsArray || statsArray.length === 0) && (
                                    <tr>
                                      <td
                                        colSpan={currentStats.length + 1}
                                        className="text-center p-3"
                                      >
                                        <div className="text-muted">
                                          <small>
                                            No detailed statistics available
                                          </small>
                                        </div>
                                      </td>
                                    </tr>
                                  )
                                );
                              })()}
                            </>
                          ) : null}
                        </>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatsTable;
